import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getDashboardStats, getProblems } from "@/lib/api";
import { Building2, Briefcase, Users, MapPin, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [problemsCount, setProblemsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsRes, problemsRes] = await Promise.all([
          getDashboardStats(),
          getProblems()
        ]);
        setStats(statsRes.data);
        setProblemsCount(problemsRes.data.length);
      } catch (error) {
        toast.error(t("admin.common.failedToLoad"));
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [t]);

  const statCards = [
    { 
      title: t("admin.dashboard.businessVerticals"), 
      value: stats?.business_verticals || 0, 
      icon: Building2, 
      color: "#0066CC",
      bg: "#E6F0FA"
    },
    { 
      title: t("admin.dashboard.problemStatements"), 
      value: problemsCount, 
      icon: AlertCircle, 
      color: "#DC2626",
      bg: "#FEE2E2"
    },
    { 
      title: t("admin.dashboard.liveProjects"), 
      value: stats?.live_projects || 0, 
      icon: Briefcase, 
      color: "#16A34A",
      bg: "#DCFCE7"
    },
    { 
      title: t("admin.dashboard.technicalExperts"), 
      value: stats?.total_technical_experts || 0, 
      icon: Users, 
      color: "#D97706",
      bg: "#FEF3C7"
    },
    { 
      title: t("admin.dashboard.branches"), 
      value: stats?.branches || 0, 
      icon: MapPin, 
      color: "#9333EA",
      bg: "#F3E8FF"
    },
  ];

  return (
    <AdminLayout title={t("admin.dashboard.title")}>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {statCards.map((stat) => (
            <Card key={stat.title} className="border-slate-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: stat.bg }}
                  >
                    <stat.icon className="h-6 w-6" style={{ color: stat.color }} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{stat.title}</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {loading ? "-" : stat.value}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Experts by Department */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">{t("admin.dashboard.expertsByDepartment")}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {stats?.expert_departments?.map((dept) => (
                    <div key={dept.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm font-medium text-slate-700">{dept.department}</span>
                      <span className="text-lg font-bold text-[#0066CC]">{dept.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">{t("admin.dashboard.branchLocations")}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {stats?.branch_details?.map((branch) => (
                    <div key={branch.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-[#0066CC] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-slate-900">{branch.name}</p>
                          <p className="text-sm text-slate-500">{branch.address}, {branch.city}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Info */}
        <Card className="border-slate-200 bg-[#E6F0FA]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="h-6 w-6 text-[#0066CC] flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">Admin Panel Guide</h3>
                <p className="text-sm text-slate-600">
                  Use the sidebar to manage verticals, problem statements, solutions, client logos, 
                  branches, and technical experts. Changes are reflected immediately on the public site.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
